import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { LogIn, UserPlus, Building2, ArrowLeft, Check, Ticket, Zap, Coins, Mail, Newspaper } from 'lucide-react';
import { toast } from "sonner";
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Checkbox } from "@/components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UserLogin = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState('login'); // login or register
    const [form, setForm] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        company: '',
        subscribe_newsletter: false
    });
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [registrationPending, setRegistrationPending] = useState(false);
    const [pendingEmail, setPendingEmail] = useState('');

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = localStorage.getItem('booking_token');
        if (!token) return;
        
        try {
            const response = await axios.get(`${API_URL}/api/booking/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            localStorage.removeItem('booking_token');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            let response;
            if (mode === 'login') {
                response = await axios.post(`${API_URL}/api/booking/auth/login`, {
                    email: form.email,
                    password: form.password
                });
                localStorage.setItem('booking_token', response.data.access_token);
                // Dispatch custom event to notify Navbar immediately
                window.dispatchEvent(new Event('authChange'));
                setUser(response.data.user);
                toast.success('Erfolgreich eingeloggt!');
            } else {
                // Registration now returns pending status
                response = await axios.post(`${API_URL}/api/booking/auth/register`, form);
                
                if (response.data.status === 'pending') {
                    // Show confirmation message
                    toast.success('Bestätigungs-E-Mail gesendet! Bitte prüfen Sie Ihr Postfach.');
                    setRegistrationPending(true);
                    setPendingEmail(form.email);
                } else if (response.data.access_token) {
                    // Legacy behavior (if token is returned)
                    localStorage.setItem('booking_token', response.data.access_token);
                    // Dispatch custom event to notify Navbar immediately
                    window.dispatchEvent(new Event('authChange'));
                    setUser(response.data.user);
                    toast.success('Erfolgreich registriert!');
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Ein Fehler ist aufgetreten';
            
            // Special handling for unconfirmed email
            if (error.response?.status === 403 && errorMessage.includes('nicht bestätigt')) {
                toast.info(errorMessage);
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('booking_token');
        // Dispatch custom event to notify Navbar immediately
        window.dispatchEvent(new Event('authChange'));
        setUser(null);
        setRegistrationPending(false);
        toast.success('Erfolgreich abgemeldet');
    };

    // Show confirmation pending screen
    if (registrationPending) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <main className="pt-32 pb-16 px-4">
                    <div className="max-w-md mx-auto text-center">
                        <div className="bg-white rounded-xl shadow-lg p-8">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Mail className="w-8 h-8 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-4">Bestätigen Sie Ihre E-Mail</h1>
                            <p className="text-gray-600 mb-6">
                                Wir haben eine Bestätigungs-E-Mail an <strong>{pendingEmail}</strong> gesendet.
                                Bitte klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                                <p className="text-sm text-gray-500">
                                    <strong>Tipp:</strong> Prüfen Sie auch Ihren Spam-Ordner, falls Sie die E-Mail nicht finden.
                                </p>
                            </div>
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setRegistrationPending(false);
                                    setMode('login');
                                }}
                                className="w-full"
                            >
                                Zurück zum Login
                            </Button>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            <div className="py-16 px-4">
                <div className="max-w-md mx-auto">
                    
                    <button 
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-gray-500 hover:text-black mb-6"
                    >
                        <ArrowLeft size={20} />
                        Zurück zur Startseite
                    </button>

                    {user ? (
                        /* Logged In View */
                        <Card>
                            <CardContent className="p-8">
                                <div className="text-center mb-6">
                                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="w-10 h-10 text-green-600" />
                                    </div>
                                    <h1 className="text-2xl font-bold">Willkommen, {user.first_name}!</h1>
                                    <p className="text-gray-500 mt-2">Sie sind eingeloggt als {user.email}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-500">Name</span>
                                            <div className="font-medium">{user.first_name} {user.last_name}</div>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Status</span>
                                            <div className={`font-medium ${user.role === 'internal' ? 'text-green-600' : ''}`}>
                                                {user.role === 'internal' ? 'Interner Nutzer' : 'Externer Nutzer'}
                                            </div>
                                        </div>
                                        {user.company && (
                                            <div className="col-span-2">
                                                <span className="text-gray-500">Firma</span>
                                                <div className="font-medium">{user.company}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {user.role === 'internal' ? (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                        <h3 className="font-semibold text-green-800 mb-1">Interner Nutzer</h3>
                                        <p className="text-sm text-green-700">
                                            Als teamWERK Mieter können Sie die Besprechungsräume kostenlos buchen!
                                        </p>
                                    </div>
                                ) : (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <h3 className="font-semibold text-blue-800 mb-1">Interner Nutzer werden?</h3>
                                        <p className="text-sm text-blue-700">
                                            Wenn Sie Mieter im teamWERK sind, kontaktieren Sie uns, um kostenlose Raumbuchungen freizuschalten.
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <Button 
                                        className="w-full bg-black hover:bg-gray-800"
                                        onClick={() => navigate('/buchung')}
                                    >
                                        <Building2 className="mr-2" size={18} />
                                        Raum buchen
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        className="w-full"
                                        onClick={handleLogout}
                                    >
                                        Abmelden
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        /* Login/Register Form */
                        <Card>
                            <CardContent className="p-8">
                                <div className="text-center mb-8">
                                    <Building2 className="w-12 h-12 mx-auto mb-4" />
                                    <h1 className="text-2xl font-bold">
                                        {mode === 'login' ? 'Anmelden' : 'Konto erstellen'}
                                    </h1>
                                    <p className="text-gray-500 mt-2">
                                        {mode === 'login' 
                                            ? 'Melden Sie sich an, um Räume zu buchen oder Tickets zu kaufen'
                                            : 'Erstellen Sie ein Konto für schnellere Buchungen'
                                        }
                                    </p>
                                </div>

                                {/* Tab Buttons */}
                                <div className="flex mb-6 border rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => setMode('login')}
                                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                                            mode === 'login' ? 'bg-black text-white' : 'bg-gray-50 text-gray-600'
                                        }`}
                                    >
                                        <LogIn size={16} />
                                        Anmelden
                                    </button>
                                    <button
                                        onClick={() => setMode('register')}
                                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                                            mode === 'register' ? 'bg-black text-white' : 'bg-gray-50 text-gray-600'
                                        }`}
                                    >
                                        <UserPlus size={16} />
                                        Registrieren
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {mode === 'register' && (
                                        <>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-sm font-medium block mb-1">Vorname *</label>
                                                    <Input 
                                                        value={form.first_name}
                                                        onChange={(e) => setForm({...form, first_name: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium block mb-1">Nachname *</label>
                                                    <Input 
                                                        value={form.last_name}
                                                        onChange={(e) => setForm({...form, last_name: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Firma (optional)</label>
                                                <Input 
                                                    value={form.company}
                                                    onChange={(e) => setForm({...form, company: e.target.value})}
                                                    placeholder="z.B. Musterfirma GmbH"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Telefon *</label>
                                                <Input 
                                                    value={form.phone}
                                                    onChange={(e) => setForm({...form, phone: e.target.value})}
                                                    placeholder="+49 123 456789"
                                                    required
                                                />
                                            </div>
                                        </>
                                    )}
                                    
                                    <div>
                                        <label className="text-sm font-medium block mb-1">E-Mail *</label>
                                        <Input 
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({...form, email: e.target.value})}
                                            placeholder="ihre@email.de"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium block mb-1">Passwort *</label>
                                        <Input 
                                            type="password"
                                            value={form.password}
                                            onChange={(e) => setForm({...form, password: e.target.value})}
                                            placeholder="••••••••"
                                            required
                                            minLength={6}
                                        />
                                        {mode === 'login' && (
                                            <button
                                                type="button"
                                                onClick={() => navigate('/passwort-vergessen')}
                                                className="text-sm text-gray-500 hover:text-black mt-2 underline"
                                            >
                                                Passwort vergessen?
                                            </button>
                                        )}
                                    </div>

                                    {mode === 'register' && (
                                        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <Checkbox 
                                                id="newsletter"
                                                checked={form.subscribe_newsletter}
                                                onCheckedChange={(checked) => setForm({...form, subscribe_newsletter: checked})}
                                                className="mt-0.5"
                                            />
                                            <div className="grid gap-1.5 leading-none">
                                                <label
                                                    htmlFor="newsletter"
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-2"
                                                >
                                                    <Newspaper size={16} className="text-gray-500" />
                                                    Newsletter abonnieren
                                                </label>
                                                <p className="text-xs text-gray-500">
                                                    Erhalten Sie News zu Events, Angeboten und dem teamWERK.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <Button 
                                        type="submit" 
                                        className="w-full bg-black hover:bg-gray-800 py-6"
                                        disabled={loading}
                                    >
                                        {loading ? 'Bitte warten...' : (mode === 'login' ? 'Anmelden' : 'Konto erstellen')}
                                    </Button>
                                </form>

                                {mode === 'register' && (
                                    <p className="text-xs text-gray-500 text-center mt-4">
                                        Mit der Registrierung stimmen Sie unseren{' '}
                                        <a href="/datenschutz" className="underline">Datenschutzbestimmungen</a> zu.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Benefits */}
                    {!user && (
                        <div className="mt-8 text-center">
                            <h3 className="font-semibold mb-4">Vorteile eines Kontos</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="bg-white p-4 rounded-lg">
                                    <div className="font-medium flex items-center gap-2">
                                        <Building2 className="w-4 h-4" /> Raumbuchung
                                    </div>
                                    <p className="text-gray-500 text-xs mt-1">Besprechungsräume buchen</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg">
                                    <div className="font-medium flex items-center gap-2">
                                        <Ticket className="w-4 h-4" /> Tickets kaufen
                                    </div>
                                    <p className="text-gray-500 text-xs mt-1">Event-Tickets erwerben</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg">
                                    <div className="font-medium flex items-center gap-2">
                                        <Zap className="w-4 h-4" /> Schneller buchen
                                    </div>
                                    <p className="text-gray-500 text-xs mt-1">Daten werden gespeichert</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg">
                                    <div className="font-medium flex items-center gap-2">
                                        <Coins className="w-4 h-4" /> Kostenlos*
                                    </div>
                                    <p className="text-gray-500 text-xs mt-1">*für teamWERK Mieter</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default UserLogin;
